import React, { ReactNode, useEffect, useState } from "react";
import styled from "styled-components";

import Checkbox from "@vfuk/core-checkbox/";
import Divider from "@vfuk/core-divider";

import { EmptyView, type EmptyViewProps } from "../EmptyView";
import { TableHeaderRow } from "./TableHeaderRow";
import { TableRow } from "./TableRow";
import { TableRowLoader } from "./TableRowLoader";
import { dataSorting } from "./sorting";
import type { ColumnType, SortType, SortedData } from "./types";
import { SortDirection } from "./types";

const FormattedText = styled.div<{
  justification: string;
  alignment: string;
}>`
  display: flex;
  justify-content: ${({ justification }) => justification};
  align-items: ${({ alignment }) => alignment};
`;

const ID = "id";
export type RowData = Record<string, unknown> & { [ID]: unknown };

/**
 * showCheckboxes show multiselect rows with the checkbox on the top (table headers) to select/deselect all too
 * selectedRows - an array of pre-selected rows (index array)
 * onSelectionChange - optional event that returns the selection onChange as an index array.
 * isLoading - show the loading data indicator
 * data - data object where each key is associated with column
 * gridTemplateColumns - string with gridTemplateColumns config
 * defaultSorting - default column index and direction for sorting
 * emptyViewConfig - an object describing an empty view when no data is given
 */
type TableProps = {
  showCheckboxes?: boolean;
  selectedRows?: Array<RowData>;
  onSelectionChange?: (selection: Array<RowData>) => void;
  isLoading?: boolean;
  data: Array<RowData>;
  gridTemplateColumns?: string;
  columns: Array<ColumnType>;
  defaultSorting?: { index: number; direction: SortDirection };
  emptyViewConfig?: EmptyViewProps;
  showRowNotes?: boolean;
  isCompact?: boolean;
  clearSelectedRows?: boolean;
  onSort?: (sortedData: SortedData) => void;
  flatStyle?: boolean;
};

export const Table = (props: TableProps) => {
  const {
    showCheckboxes = false,
    showRowNotes = false,
    isCompact = false,
    selectedRows: preSelected = [],
    onSelectionChange,
    data,
    gridTemplateColumns,
    isLoading = false,
    columns,
    defaultSorting,
    emptyViewConfig,
    clearSelectedRows,
    onSort,
    flatStyle,
  } = props;

  const sortableIndices = columns.reduce(
    (sortableIndices: number[], column, index) => {
      return column.isSortable ? [...sortableIndices, index] : sortableIndices;
    },
    []
  );

  const [selectedRows, setSelectedRows] = useState<Array<RowData>>(preSelected);

  const [sorting, setSorting] = useState<SortType>(
    defaultSorting || {
      index: 0, // Default index of column sorted by
      direction: SortDirection.ASC, // Default sorting direction
    }
  );

  const onCheck = (checked: boolean) => setSelectedRows(checked ? data : []);

  const removeFromSelected = (selectedItem: RowData) =>
    setSelectedRows(selectedRows.filter((item) => item.id !== selectedItem.id));

  const addToSelected = (selectedItem: RowData) => {
    setSelectedRows([...selectedRows, selectedItem]);
  };

  useEffect(() => {
    setSelectedRows([]);
  }, [clearSelectedRows]);

  const shouldSort =
    sorting.index >= 0 &&
    sorting.index < columns.length &&
    sorting.direction !== "NONE" &&
    sortableIndices.length > 0;

  const dataToDisplay = shouldSort
    ? data.concat().sort(dataSorting({ sorting, columns }))
    : data;

  shouldSort && onSort && onSort({ data: dataToDisplay, sorting });

  useEffect(() => {
    if (onSelectionChange) {
      const selection =
        shouldSort && selectedRows.length > 0
          ? selectedRows.concat().sort(dataSorting({ sorting, columns }))
          : selectedRows;

      onSelectionChange(selection);
    }
  }, [selectedRows, sorting, shouldSort]);

  return (
    <>
      <TableHeaderRow
        flatStyle={flatStyle}
        gridTemplateColumns={gridTemplateColumns}
        headers={columns.map((column: ColumnType) => column.label)}
        sortableHeaderIndices={sortableIndices}
        headerSorting={sorting}
        onColumnSortClick={setSorting}
        showCheckboxes={showCheckboxes}
        {...(showCheckboxes
          ? {
              checked: data.length > 0 && data.length === selectedRows.length,
              onCheck: onCheck,
            }
          : {})}
      />
      {isLoading && (
        <TableRowLoader rowCount={7} colCount={5} colWidths={["50%", "50%"]} />
      )}

      {dataToDisplay.length > 0 &&
        dataToDisplay.map((rowData: RowData, index: number) => {
          return (
            <div key={`table-row-key-${index}`}>
              {flatStyle && <Divider appearance="secondary" />}
              <TableRow
                noElevation={flatStyle}
                canViewIcon
                noMargin={isCompact}
                notes={
                  showRowNotes
                    ? (rowData.notes as string | JSX.Element)
                    : undefined
                }
                key={`${Object.entries(rowData).toString()}${index}`}
                checkbox={
                  showCheckboxes && (
                    <Checkbox
                      value=""
                      id={`check-${rowData}`}
                      name={`check-${rowData}`}
                      checked={
                        selectedRows?.find(
                          (selectedRow) => selectedRow.id === rowData.id
                        ) !== undefined
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        e.target.checked
                          ? addToSelected(rowData)
                          : removeFromSelected(rowData);
                      }}
                    >
                      {null}
                    </Checkbox>
                  )
                }
                gridTemplateColumns={gridTemplateColumns}
                rowData={Object.entries(rowData).reduce(
                  (cellsAcc: ReactNode[], [key, value], columnIndex) => {
                    const columnCell = columns[columnIndex];

                    if (key === ID || !columnCell) return cellsAcc;

                    const cellToRender =
                      columnCell?.render === undefined ? (
                        <FormattedText
                          justification={columnCell?.justification || "center"}
                          alignment={columnCell?.alignment || "flex-start"}
                        >
                          {value as string}
                        </FormattedText>
                      ) : (
                        columnCell.render(value)
                      );

                    cellsAcc.push(cellToRender);

                    return cellsAcc;
                  },
                  []
                )}
              />
            </div>
          );
        })}
      {data.length === 0 && !isLoading && emptyViewConfig && (
        <EmptyView {...emptyViewConfig} />
      )}
    </>
  );
};
